import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client';
import type { SalesCatalogue, CatalogueItem } from '../SalesLadder/useSalesCatalogue';
import { isVideoItem } from '../SalesLadder/MediaBadge';

/** Make sure the vaults owning `ids` are loaded into the catalogue, so pinned
 *  content can render its thumbnails. `hints` (content_id → vault_id, e.g. a
 *  ladder rung's `content_vaults`) loads those vaults directly; only ids with no
 *  hint that are still missing once the hinted vaults are in fall back to the
 *  server resolve — a live scan of every vault that can outlast the proxy
 *  timeout on accounts with many vaults. A failed resolve is retried a few
 *  times with backoff. Returns true while some of `ids` are still on their way
 *  in, so thumbnails can show a loading state rather than an empty tile. */
export function useResolveIds(accountId: string, cat: SalesCatalogue, ids: string[], hints?: Record<string, string>): boolean {
  const asked = useRef<Set<string>>(new Set());
  const [inFlight, setInFlight] = useState(0);
  const [retry, setRetry] = useState(0);
  const failures = useRef(0);
  const hinted = useRef<Set<string>>(new Set());
  const vaults = useRef(cat.vaults);
  // A vault-list reload clears the catalogue, so everything must be asked again.
  if (vaults.current !== cat.vaults) { vaults.current = cat.vaults; asked.current = new Set(); hinted.current = new Set(); }
  const key = ids.join(',');
  const hintKey = hints ? ids.filter((id) => hints[id]).map((id) => `${id}:${hints[id]}`).join(',') : '';
  useEffect(() => {
    if (!cat.vaults.length) return;
    const missing = ids.filter((id) => !cat.byId.has(id) && !asked.current.has(id));
    if (!missing.length) return;
    const unhinted: string[] = [];
    for (const id of missing) {
      const v = hints?.[id];
      // A vault that's already in (has items) and still lacks the id won't get it.
      const inAlready = !!v && cat.items.some((it) => it.vault_id === v);
      if (v && !hinted.current.has(id) && !inAlready && cat.vaults.some((x) => x.id === v)) { hinted.current.add(id); cat.loadVault(v); } else unhinted.push(id);
    }
    // Previews usually sit in the same vault as the pack — wait for the hinted
    // vaults before paying for a scan. A hinted id its vault didn't hold (moved,
    // or the load failed) lands here too once loading settles.
    if (!unhinted.length || cat.loading) return;
    unhinted.forEach((id) => asked.current.add(id));
    const mine = asked.current;
    setInFlight((n) => n + 1);
    api.resolveContentVaults(accountId, unhinted).then(
      (r) => { failures.current = 0; new Set(Object.values(r.vault_ids || {})).forEach((v) => cat.loadVault(v)); },
      () => {
        unhinted.forEach((id) => mine.delete(id));
        if (failures.current < RESOLVE_RETRIES) { failures.current += 1; setTimeout(() => setRetry((n) => n + 1), 1500 * failures.current); }
      },
    ).finally(() => setInFlight((n) => n - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, key, hintKey, cat.vaults, cat.loading, retry]);

  return (inFlight > 0 || cat.loading) && ids.some((id) => !cat.byId.has(id));
}

const RESOLVE_RETRIES = 3;

export const itemThumb = (it: CatalogueItem | undefined) => (it ? it.thumb_url || it.preview_url || (isVideoItem(it.type) ? null : it.full_url) : null);

/** The full-size media for the full-screen viewer. */
export const itemFull = (it: CatalogueItem | undefined) => (it ? it.full_url || it.preview_url || it.thumb_url : null);
